import React from "react";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import ToolsTabs from "../clients/Tools/kit/ToolsTabs";
import ToolsPageHeader from "../clients/Tools/kit/ToolsPageHeader";
import PlaybookBuildService from "../../services/PlaybookBuildService";
import PlaybookEditService from "../../services/PlaybookEditService";
import UtilityService from "../../services/UtilityService";
import PlaybookInstructionsPanel from "./PlaybookInstructionsPanel";
import PlaybookVersionsPanel from "./PlaybookVersionsPanel";
import PlaybookSettingsPanel from "./PlaybookSettingsPanel";
import PlaybookCorrectionsPanel from "./PlaybookCorrectionsPanel";
import AgentSourceIcon from "./AgentSourceIcon";
import PlaybookCategoryIcon from "./PlaybookCategoryIcon";
import RunPlaybookFlow from "./RunPlaybookFlow";

import "./playbook-detail.css";

const TABS = {
	instructions: "instructions",
	versions: "versions",
	corrections: "corrections",
	settings: "settings",
};

class PlaybookDetail extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			agent: null,
			activeTab: TABS.instructions,
			isLoading: true,
			loadError: null,
			isChoosingRun: false,
		};
	}

	componentDidMount() {
		this.fetchAgent();
	}

	fetchAgent = async () => {
		const { playbookId } = this.props;
		try {
			const agent = await PlaybookEditService.getPlaybookForEditing({ playbookId });
			await this.update({ agent, isLoading: false, loadError: null });
		} catch (error) {
			await this.update({ isLoading: false, loadError: UtilityService.describeRequestError({ error, fallback: "This playbook could not be loaded." }) });
		}
	};

	onAgentChanged = ({ agent }) => {
		this.update({ agent });
	};

	renderJudgments() {
		const { agent } = this.state;
		const { judgments, note } = PlaybookBuildService.describeDecisionPoints({ decisionPoints: agent.decisionPoints });
		return (
			<div className="tk-chips">
				<span className="tk-chips__label">Judges</span>
				{judgments.map(judgment => (
					<span key={judgment.key} className="tk-chip tk-chip--muted" title={judgment.factsText}>
						<Icon.GitBranch size={12} />
						{judgment.question}
					</span>
				))}
				{note && <span className="tk-chip tk-chip--muted">{note}</span>}
			</div>
		);
	}

	renderFacts() {
		const { agent } = this.state;
		const credits = [agent.category && agent.category.name, `Created by ${agent.createdBy}`];
		if (agent.contributors.length > 0) credits.push(`Contributors: ${agent.contributors.join(", ")}`);
		if (agent.savedBy) credits.push(`v${agent.versionNumber} saved by ${agent.savedBy}`);

		return (
			<React.Fragment>
				<div className="tools-page-header__credits">{credits.filter(Boolean).join(" · ")}</div>
				{agent.sources.length > 0 && (
					<div className="tk-chips">
						<span className="tk-chips__label">Reads</span>
						{agent.sources.map(source => (
							<span key={source.key} className="tk-chip" title={source.reads}>
								<AgentSourceIcon icon={source.icon} size="small" />
								{source.label}
								<span className="tk-chip__note">{source.isRequired ? "required" : "helpful"}</span>
							</span>
						))}
					</div>
				)}
				{this.renderJudgments()}
				{agent.skills.length > 0 && (
					<div className="tk-chips">
						<span className="tk-chips__label">Follows</span>
						{agent.skills.map(skill => (
							<span key={skill.id} className="tk-chip tk-chip--muted" title={skill.description}>
								<Icon.BookOpen size={12} />
								{skill.name} v{skill.versionNumber}
							</span>
						))}
					</div>
				)}
			</React.Fragment>
		);
	}

	render() {
		const { onBack, onChangeWithGuide, playbooks, history, onOpenRun } = this.props;
		const { agent, activeTab, isLoading, loadError, isChoosingRun } = this.state;
		if (isLoading) return <div className="tk-empty">Loading playbook…</div>;
		if (loadError) return <div className="tk-notice tk-notice--danger">{loadError}</div>;

		const tabs = [
			{
				key: TABS.instructions,
				label: "Instructions",
				count: agent.pendingChangeCount || undefined,
				countTone: agent.pendingChangeCount ? "warning" : undefined,
				title: agent.pendingChangeCount ? `${agent.pendingChangeCount} changes waiting for a decision` : undefined,
			},
			{ key: TABS.versions, label: "Versions", count: agent.versionNumber },
			{ key: TABS.corrections, label: "Corrections" },
			{ key: TABS.settings, label: "Settings" },
		];
		const badges = (
			<React.Fragment>
				<span className={agent.isBuiltin ? "tk-badge tk-badge--accent" : "tk-badge tk-badge--neutral"}>{agent.isBuiltin ? "Built-in" : "Custom"}</span>
				<span className="tk-badge tk-badge--neutral">v{agent.versionNumber}</span>
			</React.Fragment>
		);

		return (
			<div className="playbook-detail">
				<ToolsPageHeader
					onBack={onBack}
					backLabel="Back to playbooks"
					lead={<PlaybookCategoryIcon icon={agent.category ? agent.category.icon : null} size="large" />}
					title={agent.name}
					badges={badges}
					subtitle={agent.description}
					facts={this.renderFacts()}
					actions={
						<React.Fragment>
							{agent.ownsAgent && (
								<button type="button" className="tk-button tk-button--secondary" onClick={onChangeWithGuide}>
									<Icon.MessageSquare size={14} />
									Change with the guide
								</button>
							)}
							<button type="button" className="tk-button tk-button--primary" onClick={() => this.update({ isChoosingRun: true })}>
								<Icon.Play size={14} />
								Run playbook
							</button>
						</React.Fragment>
					}
				/>
				<ToolsTabs tabs={tabs} activeKey={activeTab} onSelect={tab => this.update({ activeTab: tab })} />
				<div className="playbook-detail__body">
					{activeTab === TABS.instructions && <PlaybookInstructionsPanel agent={agent} onChanged={this.onAgentChanged} />}
					{activeTab === TABS.versions && (
						<PlaybookVersionsPanel playbookId={agent.playbookId} currentVersionId={agent.versionId} onChanged={this.onAgentChanged} />
					)}
					{activeTab === TABS.corrections && <PlaybookCorrectionsPanel playbookId={agent.playbookId} />}
					{activeTab === TABS.settings && <PlaybookSettingsPanel playbookId={agent.playbookId} />}
				</div>
				<RunPlaybookFlow
					open={isChoosingRun}
					playbooks={playbooks}
					initialPlaybookId={agent.playbookId}
					history={history}
					onClose={() => this.update({ isChoosingRun: false })}
					onOpenRun={onOpenRun}
				/>
			</div>
		);
	}
}

export default PlaybookDetail;
